// src/pages/PerceivedVsObserved.js
// Team Perspective vs Consultant Observations — comparison view
// Shows team assessment scores (1–5) normalized to 0–100% alongside
// consultant observed rates (X/5 meetings = 0–100%) for the last 5 meetings.

import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  collection, query, where, getDocs, doc, getDoc, orderBy, limit,
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES, withCurrentLabels } from '../utils/surveyQuestions';
import PageLoading from '../components/PageLoading';

// Normalize a 1–5 score to 0–100
function normalizeScore(score) {
  return Math.round(((score - 1) / 4) * 100);
}

// Compute per-category averages from team assessment responses
function computeTeamScores(responses, categories) {
  const catScores = {};
  categories.forEach(cat => {
    const scores = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) scores.push(r.answers[q.id]);
      });
    });
    catScores[cat.id] = scores.length
      ? scores.reduce((a, b) => a + b, 0) / scores.length
      : null;
  });
  return catScores;
}

// Compute per-category observation rates from consultant observations
// Returns 0–1 (fraction of times each question was observed across meetings)
function computeObservationRates(observations, categories) {
  const catRates = {};
  categories.forEach(cat => {
    const rates = [];
    cat.questions.forEach(q => {
      let observedCount = 0;
      let total = 0;
      observations.forEach(obs => {
        if (obs.observed && obs.observed[q.id] !== undefined) {
          total++;
          if (obs.observed[q.id]) observedCount++;
        }
      });
      if (total > 0) rates.push(observedCount / total);
    });
    catRates[cat.id] = rates.length
      ? rates.reduce((a, b) => a + b, 0) / rates.length
      : null;
  });
  return catRates;
}

// Custom tooltip for the bar chart
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'white',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '12px 16px',
      fontSize: '0.82rem',
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--ink)' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: p.fill }} />
          <span style={{ color: 'var(--ink-muted)' }}>{p.name}:</span>
          <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{p.value}%</span>
        </div>
      ))}
      {payload.length === 2 && (
        <div style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          color: 'var(--ink-muted)',
          fontSize: '0.75rem',
        }}>
          Gap: {Math.abs(payload[0].value - payload[1].value)}%
          {payload[0].value > payload[1].value
            ? ' (team perceives higher)'
            : ' (consultant observes more)'}
        </div>
      )}
    </div>
  );
}

export default function PerceivedVsObserved() {
  const { userProfile } = useAuth();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [teamScores, setTeamScores] = useState(null);
  const [obsRates, setObsRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetingCount, setMeetingCount] = useState(0);
  const [responseCount, setResponseCount] = useState(0);
  const [expandedCat, setExpandedCat] = useState(null);
  const [chartView, setChartView] = useState('bar'); // 'bar' | 'radar'

  const teamId = userProfile?.teamId;

  useEffect(() => {
    if (teamId) loadData();
  }, [teamId]);

  async function loadData() {
    try {
      // Load team custom questions
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      let cats = DEFAULT_CATEGORIES;
      if (teamSnap.exists() && teamSnap.data().customQuestions) {
        cats = withCurrentLabels(teamSnap.data().customQuestions);
        setCategories(cats);
      }

      // Load team assessment responses
      const responsesSnap = await getDocs(
        query(collection(db, 'responses'), where('teamId', '==', teamId))
      );
      const responses = responsesSnap.docs.map(d => d.data());
      setResponseCount(responses.length);

      // Load last 5 completed meetings
      const meetingsSnap = await getDocs(
        query(
          collection(db, 'meetings'),
          where('teamId', '==', teamId),
          where('status', 'in', ['completed', 'reviewed']),
          orderBy('meetingDate', 'desc'),
          limit(5)
        )
      );
      const meetingIds = meetingsSnap.docs.map(d => d.id);
      setMeetingCount(meetingIds.length);

      // Load consultant observations for those meetings
      let observations = [];
      if (meetingIds.length > 0) {
        // Firestore 'in' supports up to 10 items — safe for 5
        const obsSnap = await getDocs(
          query(
            collection(db, 'consultantObservations'),
            where('meetingId', 'in', meetingIds)
          )
        );
        observations = obsSnap.docs.map(d => d.data());
      }

      // Compute scores
      const tScores = computeTeamScores(responses, cats);
      const oRates = computeObservationRates(observations, cats);

      setTeamScores(tScores);
      setObsRates(oRates);
    } catch (err) {
      console.error('Error loading perceived vs observed data:', err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <PageLoading text="Loading comparison data..." />;

  if (!teamScores || !obsRates) return (
    <div className="fade-up">
      <div className="page-header">
        <h2>Team Perspective vs Consultant Observations</h2>
        <p>Not enough data yet. Complete team assessments and record at least one meeting.</p>
      </div>
    </div>
  );

  // Build chart data — both normalized to 0–100%
  const overviewData = categories.map(cat => ({
    name: cat.label,
    shortName: cat.label.split(' ').slice(0, 2).join(' '),
    color: cat.color,
    teamPct: teamScores[cat.id] != null ? normalizeScore(teamScores[cat.id]) : null,
    obsPct: obsRates[cat.id] != null ? Math.round(obsRates[cat.id] * 100) : null,
    rawTeam: teamScores[cat.id],
    rawObs: obsRates[cat.id],
  }));

  // Drill-down data for expanded category
  const drillCat = expandedCat ? categories.find(c => c.id === expandedCat) : null;
  const drillData = drillCat ? drillCat.questions.map(q => {
    const teamQScores = [];
    // We don't have per-question team data reloaded here — approximate from category
    // For a full implementation this would query per question; using category average as proxy
    return {
      name: q.text.length > 40 ? q.text.substring(0, 38) + '…' : q.text,
      fullText: q.text,
    };
  }) : [];

  const radarData = overviewData.map(d => ({
    category: d.shortName,
    'Team Perspective': d.teamPct,
    'Consultant Observed': d.obsPct,
  }));

  const hasGap = overviewData.some(d => d.teamPct != null && d.obsPct != null);
  const avgGap = hasGap
    ? Math.round(
        overviewData
          .filter(d => d.teamPct != null && d.obsPct != null)
          .reduce((sum, d) => sum + Math.abs(d.teamPct - d.obsPct), 0) /
        overviewData.filter(d => d.teamPct != null && d.obsPct != null).length
      )
    : null;

  return (
    <div className="fade-up">
      {/* Header */}
      <div className="page-header">
        <h2>Team Perspective vs Consultant Observations</h2>
        <p>
          How the team perceives meeting quality compared to what was directly observed — based on{' '}
          {responseCount} assessment{responseCount !== 1 ? 's' : ''} and{' '}
          {meetingCount} meeting{meetingCount !== 1 ? 's' : ''}.
        </p>
      </div>

      {/* Legend + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: '#2a3d5b' }} />
          <span style={{ fontSize: '0.85rem', color: 'var(--ink)', fontWeight: 600 }}>Team Perspective</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>— normalized 1–5 → 0–100%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 14, height: 14, borderRadius: 3, background: '#cba43a' }} />
          <span style={{ fontSize: '0.85rem', color: 'var(--ink)', fontWeight: 600 }}>Consultant Observed</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>— % of meetings where behavior was observed</span>
        </div>
        {avgGap != null && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>Avg gap:</span>
            <span style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.4rem',
              color: avgGap > 20 ? '#e63946' : avgGap > 10 ? '#f4a261' : '#2d6a4f',
            }}>{avgGap}%</span>
          </div>
        )}
      </div>

      {/* Chart view toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setChartView('bar')}
          className={chartView === 'bar' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
        >
          Bar Chart
        </button>
        <button
          onClick={() => setChartView('radar')}
          className={chartView === 'radar' ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
        >
          Radar Chart
        </button>
      </div>

      {/* Overview chart */}
      <div className="card" style={{ marginBottom: 28 }}>
        <div className="card-title">All Categories — Overview</div>
        <div className="card-subtitle">
          {chartView === 'bar'
            ? 'Side-by-side comparison across all 4 categories. Click a category bar to drill down.'
            : 'Overlapping radar — gap between shapes indicates perception vs reality difference.'}
        </div>

        {chartView === 'bar' ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={overviewData.map(d => ({
                name: d.shortName,
                id: d.name,
                'Team Perspective': d.teamPct,
                'Consultant Observed': d.obsPct,
              }))}
              margin={{ top: 10, right: 10, left: -10, bottom: 10 }}
              onClick={(data) => {
                if (data?.activePayload) {
                  const clickedName = data.activePayload[0]?.payload?.id;
                  const cat = categories.find(c => c.label === clickedName);
                  if (cat) setExpandedCat(expandedCat === cat.id ? null : cat.id);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eee8" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8888aa' }} />
              <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '0.8rem', paddingTop: 12 }}
              />
              <Bar dataKey="Team Perspective" fill="#2a3d5b" radius={[5, 5, 0, 0]} />
              <Bar dataKey="Consultant Observed" fill="#cba43a" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#8888aa' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 9 }} />
              <Radar
                name="Team Perspective"
                dataKey="Team Perspective"
                stroke="#2a3d5b"
                fill="#2a3d5b"
                fillOpacity={0.2}
                strokeWidth={2}
              />
              <Radar
                name="Consultant Observed"
                dataKey="Consultant Observed"
                stroke="#cba43a"
                fill="#cba43a"
                fillOpacity={0.2}
                strokeWidth={2}
              />
              <Legend wrapperStyle={{ fontSize: '0.8rem', paddingTop: 12 }} />
              <Tooltip formatter={(val) => `${val}%`} />
            </RadarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Category cards with drill-down */}
      <div style={{ marginBottom: 8 }}>
        <div className="card-title" style={{ marginBottom: 4 }}>Drill Down by Category</div>
        <div className="card-subtitle" style={{ marginBottom: 20 }}>
          Click a category to see the detailed question-level comparison.
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {overviewData.map(d => {
          const cat = categories.find(c => c.label === d.name);
          const isExpanded = expandedCat === cat?.id;
          const gap = d.teamPct != null && d.obsPct != null ? d.teamPct - d.obsPct : null;
          const gapColor = gap == null ? 'var(--ink-muted)'
            : gap > 15 ? '#e63946'
            : gap > 0 ? '#f4a261'
            : '#2d6a4f';

          return (
            <div key={d.name} className="card" style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setExpandedCat(isExpanded ? null : cat?.id)}
            >
              {/* Category header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>{d.name}</span>

                {/* Team score pill */}
                <div style={{ textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Team</div>
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.1rem',
                    color: '#2a3d5b',
                    fontWeight: 700,
                  }}>
                    {d.teamPct != null ? `${d.teamPct}%` : '—'}
                  </div>
                  {d.rawTeam && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--ink-muted)' }}>
                      ({d.rawTeam.toFixed(1)}/5)
                    </div>
                  )}
                </div>

                {/* Observed score pill */}
                <div style={{ textAlign: 'center', minWidth: 80 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Observed</div>
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.1rem',
                    color: '#cba43a',
                    fontWeight: 700,
                  }}>
                    {d.obsPct != null ? `${d.obsPct}%` : '—'}
                  </div>
                  {d.rawObs != null && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--ink-muted)' }}>
                      ({Math.round(d.rawObs * meetingCount)}/{meetingCount} mtgs)
                    </div>
                  )}
                </div>

                {/* Gap indicator */}
                <div style={{ textAlign: 'center', minWidth: 70 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Gap</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: gapColor, fontWeight: 700 }}>
                    {gap != null ? `${gap > 0 ? '+' : ''}${gap}%` : '—'}
                  </div>
                </div>

                <span style={{ color: 'var(--ink-muted)', fontSize: '0.8rem', marginLeft: 4 }}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </div>

              {/* Progress bars */}
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {/* Team bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', width: 100, flexShrink: 0 }}>Team Perspective</span>
                  <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4 }}>
                    <div style={{
                      height: '100%',
                      width: `${d.teamPct || 0}%`,
                      background: '#2a3d5b',
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
                {/* Observed bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', width: 100, flexShrink: 0 }}>Observed</span>
                  <div style={{ flex: 1, height: 8, background: 'var(--border)', borderRadius: 4 }}>
                    <div style={{
                      height: '100%',
                      width: `${d.obsPct || 0}%`,
                      background: '#cba43a',
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }} />
                  </div>
                </div>
              </div>

              {/* Expanded drill-down — per-question bar chart */}
              {isExpanded && cat && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--ink-muted)', marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Question Breakdown — {cat.label}
                  </div>
                  {cat.questions.map((q, qi) => {
                    // Per-question observed rate averaged across meetings
                    return (
                      <div key={q.id} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '8px 0',
                        borderBottom: qi < cat.questions.length - 1 ? '1px solid var(--bg-soft)' : 'none',
                      }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginTop: 2, width: 18, flexShrink: 0 }}>
                          {qi + 1}.
                        </span>
                        <span style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', flex: 1, lineHeight: 1.5 }}>
                          {q.text}
                        </span>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: 12, fontSize: '0.75rem', color: 'var(--ink-muted)', fontStyle: 'italic' }}>
                    Per-question breakdown requires individual question data in a future update.
                    Category average shown above.
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Interpretation guide */}
      <div className="card" style={{ marginTop: 28 }}>
        <div className="card-title">How to Read This</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {[
            { color: '#2d6a4f', label: 'Small gap (0–10%)', desc: 'Team perception closely matches observed reality. Strong signal.' },
            { color: '#f4a261', label: 'Moderate gap (10–20%)', desc: 'Some misalignment. Worth exploring in team conversation.' },
            { color: '#e63946', label: 'Large gap (20%+)', desc: 'Significant disconnect. High priority area for coaching.' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, marginTop: 4, flexShrink: 0 }} />
              <div>
                <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--ink)' }}>{item.label} — </span>
                <span style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>{item.desc}</span>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 4, fontSize: '0.78rem', color: 'var(--ink-muted)', lineHeight: 1.6, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <strong>Note on normalization:</strong> Team scores (1–5 scale) are converted to 0–100% using the formula{' '}
            <code style={{ background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 3 }}>((score − 1) / 4) × 100</code>.
            Consultant observation rates are the percentage of meetings where each behavior was observed.
            This allows direct comparison on a common scale.
          </div>
        </div>
      </div>
    </div>
  );
}
